import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import DateRangeSelector from '../components/DateRangeSelector';
import { 
  Add as AddIcon, 
  Visibility as ViewIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Done as DoneIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import InvoicePreview from './InvoicePreview';
import { formatVendorCurrency } from '../utils/currency';

interface VendorInvoice {
  id: number;
  vendor_firm_id: number;
  vendor_user_id: number;
  invoice_number: string;
  invoice_date: string;
  period_start: string;
  period_end: string;
  opening_balance: number;
  total_billing: number;
  total_payments: number;
  net_amount: number;
  currency: string;
  billing_currency?: string | null;
  conversion_rate?: number | null;
  original_amount?: number | null;
  converted_amount?: number | null;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'final' | 'sent' | 'paid' | 'overdue';
  notes: string;
  total_paid: number;
  remaining_balance: number;
  payment_status: 'unpaid' | 'partial' | 'paid';
  vendor_firm: {
    id: number;
    name: string;
  };
  vendor_user: {
    id: number;
    name: string;
    currency?: string | null;
    billing_currency?: string | null;
  };
  created_by: {
    id: number;
    name: string;
  };
  items: VendorInvoiceItem[];
}

interface VendorInvoiceItem {
  id: number;
  case_title: string;
  billing_type: string;
  rate: number;
  quantity: number;
  amount: number;
  description: string;
}

interface Vendor {
  id: number;
  name: string;
  email: string;
}

const VendorInvoices: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<VendorInvoice[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    vendor_firm_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: '',
  });
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<VendorInvoice | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    reference_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
    tds_rate: 10.00
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    vendor_user_id: '',
    period_start: new Date().toISOString().split('T')[0].substring(0, 7) + '-01', // First day of current month
    period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0], // Last day of current month
    notes: '',
    conversion_rate: '',
  });
  const [selectedVendorCurrency, setSelectedVendorCurrency] = useState<{currency?: string, billing_currency?: string} | null>(null);
  const [generateDateRangeErrors, setGenerateDateRangeErrors] = useState<{start?: string, end?: string}>({});
  const [toastNotification, setToastNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    loadInvoices();
    loadVendors();
  }, [filters]);

  const handleToastNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToastNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseToast = () => {
    setToastNotification(prev => ({ ...prev, open: false }));
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.vendor_firm_id) params.append('vendor_firm_id', filters.vendor_firm_id);
      if (filters.month) params.append('month', filters.month.toString());
      if (filters.year) params.append('year', filters.year.toString());
      if (filters.status) params.append('status', filters.status);

      const response = await api.get(`/vendor-invoices?${params}`);
      if (response.data.success) {
        console.log('Invoices data:', response.data.data.data);
        console.log('First invoice vendor info:', response.data.data.data[0]?.vendor_firm, response.data.data.data[0]?.vendor_user);
        setInvoices(response.data.data.data || []);
      }
    } catch (err: any) {
      console.error('Error loading invoices:', err);
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await api.get('/vendor-invoices/vendors');
      if (response.data.success) {
        setVendors(response.data.data);
        if (response.data.data.length === 1 && !generateForm.vendor_user_id) {
          setGenerateForm({ ...generateForm, vendor_user_id: String(response.data.data[0].id) });
        }
      }
    } catch (err: any) {
      console.error('Failed to load vendors:', err);
      setError(err.response?.data?.message || 'Failed to load vendors');
    }
  };

  const loadVendorUserDetails = async (userId: string) => {
    try {
      const response = await api.get(`/users/${userId}`);
      if (response.data.success) {
        const user = response.data.data;
        setSelectedVendorCurrency({
          currency: user.currency || 'USD',
          billing_currency: user.billing_currency || 'USD'
        });
      }
    } catch (err: any) {
      console.error('Failed to load vendor user details:', err);
    }
  };

  const handlePreview = async () => {
    if (!generateForm.vendor_user_id) {
      setError('Please select a vendor user');
      return;
    }

    try {
      const response = await api.get('/vendor-invoices/preview', {
        params: {
          vendor_user_id: generateForm.vendor_user_id,
          period_start: generateForm.period_start,
          period_end: generateForm.period_end,
        }
      });

      if (response.data.success) {
        setPreviewData(response.data.data);
        setShowPreviewDialog(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load preview');
    }
  };

  const handleGenerate = async () => {
    try {
      const { vendor_user_id, period_start, period_end, notes, conversion_rate } = generateForm as any;
      const payload: any = {
        vendor_user_id,
        period_start,
        period_end,
        notes,
      };
      
      // Include conversion_rate if provided
      if (conversion_rate && parseFloat(conversion_rate) > 0) {
        payload.conversion_rate = parseFloat(conversion_rate);
      }
      
      const response = await api.post('/vendor-invoices/generate', payload);
      
      if (response.data.success) {
        setSuccess('Invoice generated successfully');
        setShowGenerateDialog(false);
        setGenerateForm({
          vendor_user_id: '',
          period_start: new Date().toISOString().split('T')[0].substring(0, 7) + '-01',
          period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
          notes: '',
          conversion_rate: '',
        });
        setSelectedVendorCurrency(null);
        
        // Check if conversion is needed
        if (response.data.needs_conversion) {
          handleToastNotification(
            `Invoice created. Please provide conversion rate for ${response.data.billing_currency}`,
            'warning'
          );
        }
        setGenerateDateRangeErrors({});
        loadInvoices();
      }
    } catch (err: any) {
      // Handle date range validation errors
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        if (errors.period_start || errors.period_end) {
          setGenerateDateRangeErrors({
            start: errors.period_start?.[0],
            end: errors.period_end?.[0]
          });
          
          // Show toast notification for date range conflict
          if (err.response?.data?.message?.includes('date range')) {
            handleToastNotification(err.response.data.message, 'warning');
          }
        }
      }
      
      setError(err.response?.data?.message || 'Failed to generate invoice');
    }
  };

  const handleApproveInvoice = async (invoiceId: number) => {
    try {
      const response = await api.post(`/vendor-invoices/${invoiceId}/approve-by-admin`);
      if (response.data.success) {
        setSuccess('Invoice finalized successfully');
        loadInvoices();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to finalize invoice');
    }
  };

  const handleRejectInvoice = async (invoiceId: number) => {
    const rejectionReason = prompt('Please provide a reason for rejection:');
    if (!rejectionReason) return;

    try {
      const response = await api.post(`/vendor-invoices/${invoiceId}/reject-by-admin`, {
        rejection_reason: rejectionReason
      });
      if (response.data.success) {
        setSuccess('Invoice rejected successfully');
        loadInvoices();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject invoice');
    }
  };

  const handleFinalizeInvoice = async (invoiceId: number) => {
    try {
      const response = await api.post(`/vendor-invoices/${invoiceId}/finalize`);
      if (response.data.success) {
        setSuccess('Invoice finalized successfully');
        loadInvoices();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to finalize invoice');
    }
  };

  const handleDownloadPdf = async (invoiceId: number) => {
    try {
      const response = await api.get(`/vendor-invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'invoice.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('Invoice downloaded successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download invoice');
    }
  };

  const handleRecordPayment = (invoice: VendorInvoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      amount: invoice.remaining_balance.toString(),
      payment_method: 'bank_transfer',
      reference_number: '',
      payment_date: new Date().toISOString().split('T')[0],
      notes: '',
      tds_rate: 10.00
    });
    setShowPaymentDialog(true);
  };

  const handleSubmitPayment = async () => {
    if (!selectedInvoice) return;
    
    try {
      setPaymentLoading(true);
      const response = await api.post(`/vendor-invoices/${selectedInvoice.id}/payments`, paymentForm);
      if (response.data.success) {
        setSuccess('Payment recorded successfully');
        setShowPaymentDialog(false);
        setSelectedInvoice(null);
    setPaymentForm({
      amount: '',
      payment_method: 'bank_transfer',
      reference_number: '',
      payment_date: new Date().toISOString().split('T')[0],
      notes: '',
      tds_rate: 10.00
    });
        loadInvoices(); // Reload to get updated payment status
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'sent': return 'warning';
      case 'draft': return 'default';
      case 'pending_approval': return 'warning';
      case 'approved': return 'info';
      case 'rejected': return 'error';
      case 'final': return 'success';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number, invoice?: VendorInvoice) => {
    // Use billing_currency if conversion is present, otherwise use vendor currency
    const currency = invoice?.billing_currency && invoice?.currency && invoice?.billing_currency !== invoice?.currency && invoice?.conversion_rate
      ? invoice.billing_currency
      : (invoice?.vendor_user?.currency || invoice?.currency || 'USD');
    return formatVendorCurrency(amount, currency);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: 2
      }}>
        <Typography variant="h4">Vendor Invoices</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowGenerateDialog(true)}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Generate Invoice
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Invoices
            </Typography>
            <Typography variant="h4">
              {invoices.length}
            </Typography>
            </CardContent>
          </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Amount
            </Typography>
            <Typography variant="h4">
              {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.net_amount || 0), 0), invoices[0])}
            </Typography>
            </CardContent>
          </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Paid Invoices
            </Typography>
            <Typography variant="h4">
              {invoices.filter(inv => inv.status === 'paid').length}
            </Typography>
            </CardContent>
          </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Pending Amount
            </Typography>
            <Typography variant="h4">
              {formatCurrency(invoices.filter(inv => inv.status !== 'paid').reduce((sum, inv) => sum + (inv.net_amount || 0), 0), invoices[0])}
            </Typography>
            </CardContent>
          </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Vendor Firm</InputLabel>
            <Select
              value={filters.vendor_firm_id}
              onChange={(e) => setFilters({ ...filters, vendor_firm_id: e.target.value })}
              label="Vendor Firm"
            >
              <MenuItem value="">All Vendors</MenuItem>
              {vendors.map(vendor => (
                <MenuItem key={vendor.id} value={vendor.id}>{vendor.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Month</InputLabel>
            <Select
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: Number(e.target.value) })}
              label="Month"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <MenuItem key={month} value={month}>
                  {new Date(2024, month - 1).toLocaleDateString('en-US', { month: 'long' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Year"
            type="number"
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: Number(e.target.value) })}
          />
          
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending_approval">Pending Approval</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="final">Final</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Invoices Table */}
      <Paper>
        <TableContainer
          sx={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Invoice #</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Vendor Firm</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Vendor User</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>Period</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Total Billing</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Net Amount</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Total Paid</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>Payment Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Created</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No invoices found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Typography fontWeight="bold">
                        {invoice.invoice_number || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{invoice.vendor_firm?.name || 'N/A'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{invoice.vendor_user?.name || 'N/A'}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {invoice.period_start ? new Date(invoice.period_start).toLocaleDateString() : 'N/A'} - {invoice.period_end ? new Date(invoice.period_end).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{formatCurrency(invoice.total_billing || 0, invoice)}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Typography fontWeight="bold">
                        {formatCurrency(invoice.net_amount || 0, invoice)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Typography fontWeight="medium" color="success.main">
                        {formatCurrency(invoice.total_paid || 0, invoice)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status === 'pending_approval' ? 'AWAITING PMP APPROVAL' : 
                               invoice.status === 'final' ? 'FINALIZED' :
                               invoice.status.toUpperCase()}
                        color={getStatusColor(invoice.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Chip
                        label={invoice.payment_status === 'paid' ? 'PAID' :
                               invoice.payment_status === 'partial' ? 'PARTIAL' :
                               'UNPAID'}
                        color={invoice.payment_status === 'paid' ? 'success' :
                               invoice.payment_status === 'partial' ? 'warning' :
                               'error'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Invoice">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/vendor-invoices/${invoice.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {invoice.status === 'pending_approval' && (
                          <>
                                            <Tooltip title="Finalize Invoice">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleApproveInvoice(invoice.id)}
                  >
                    <CheckCircleIcon />
                  </IconButton>
                </Tooltip>
                            <Tooltip title="Reject Invoice">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRejectInvoice(invoice.id)}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {invoice.status === 'approved' && (
                          <Tooltip title="Finalize Invoice">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleFinalizeInvoice(invoice.id)}
                            >
                              <DoneIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(invoice.status === 'final' || invoice.status === 'approved') && (
                          <Tooltip title="Download PDF">
                            <IconButton 
                              size="small"
                              onClick={() => handleDownloadPdf(invoice.id)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {invoice.remaining_balance > 0 && (
                          <Tooltip title="Record Payment">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleRecordPayment(invoice)}
                            >
                              <PaymentIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Generate Invoice Dialog */}
      <Dialog open={showGenerateDialog} onClose={() => setShowGenerateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Generate Vendor Invoice</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Vendor User</InputLabel>
              <Select
                value={generateForm.vendor_user_id}
                onChange={async (e) => {
                  const userId = e.target.value;
                  setGenerateForm({ ...generateForm, vendor_user_id: userId, conversion_rate: '' });
                  if (userId) {
                    await loadVendorUserDetails(userId);
                  } else {
                    setSelectedVendorCurrency(null);
                  }
                }}
                label="Vendor User"
              >
                {vendors.map(vendor => (
                  <MenuItem key={vendor.id} value={vendor.id}>{vendor.name} ({vendor.email})</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Show conversion rate input if currencies differ */}
            {selectedVendorCurrency && 
             selectedVendorCurrency.currency !== selectedVendorCurrency.billing_currency && (
              <TextField
                fullWidth
                label={`Conversion Rate (${selectedVendorCurrency.currency} to ${selectedVendorCurrency.billing_currency})`}
                type="number"
                value={generateForm.conversion_rate}
                onChange={(e) => setGenerateForm({ ...generateForm, conversion_rate: e.target.value })}
                inputProps={{ min: 0.0001, step: 0.0001 }}
                helperText={`Enter the conversion rate from ${selectedVendorCurrency.currency} to ${selectedVendorCurrency.billing_currency}`}
                required
              />
            )}
            
            {/* Opening Balance removed - computed automatically on backend */}
            
            <Box sx={{ gridColumn: '1 / -1' }}>
              <DateRangeSelector
                periodStart={generateForm.period_start}
                periodEnd={generateForm.period_end}
                onPeriodStartChange={(value) => setGenerateForm({ ...generateForm, period_start: value })}
                onPeriodEndChange={(value) => setGenerateForm({ ...generateForm, period_end: value })}
                vendorUserId={generateForm.vendor_user_id ? parseInt(generateForm.vendor_user_id) : undefined}
                showValidation={true}
                showToastNotifications={true}
                error={generateDateRangeErrors}
                onToastNotification={handleToastNotification}
                helperText={{
                  start: 'First day of the billing period',
                  end: 'Last day of the billing period'
                }}
              />
            </Box>
            
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={generateForm.notes}
              onChange={(e) => setGenerateForm({ ...generateForm, notes: e.target.value })}
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
          <Button onClick={handlePreview} variant="outlined">
            Preview
          </Button>
          <Button 
            onClick={handleGenerate} 
            variant="contained"
            disabled={!generateForm.vendor_user_id || !generateForm.period_start || !generateForm.period_end}
          >
            Generate Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Preview Component */}
      <InvoicePreview
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        invoice={previewData}
        onGenerate={handleGenerate}
        allowRateEditing={true}
        onInvoiceUpdated={(updatedInvoice) => {
          setPreviewData(updatedInvoice);
        }}
      />

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon color="primary" />
            Record Payment
            {selectedInvoice && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                - Invoice #{selectedInvoice.invoice_number}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Payment Amount"
              type="number"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
              inputProps={{ min: 0.01, max: selectedInvoice?.remaining_balance || 0, step: 0.01 }}
              helperText={`Maximum: ${formatCurrency(selectedInvoice?.remaining_balance || 0, selectedInvoice || undefined)}`}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="TDS Rate (%)"
              type="number"
              value={paymentForm.tds_rate}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, tds_rate: parseFloat(e.target.value) || 0 }))}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              helperText={`TDS Amount: ${formatCurrency((parseFloat(paymentForm.amount) || 0) * (paymentForm.tds_rate / 100), selectedInvoice || undefined)}`}
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentForm.payment_method}
                label="Payment Method"
                onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
              >
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="check">Check</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="online_transfer">Online Transfer</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Reference Number"
              value={paymentForm.reference_number}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
              helperText="Transaction ID, Check number, etc."
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Payment Date"
              type="date"
              value={paymentForm.payment_date}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
              helperText="Optional notes about the payment"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitPayment}
            variant="contained"
            disabled={!paymentForm.amount || paymentLoading}
            startIcon={paymentLoading ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {paymentLoading ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Toast Notification for Date Range Validation */}
      <Snackbar
        open={toastNotification.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseToast} 
          severity={toastNotification.severity} 
          sx={{ width: '100%' }}
        >
          {toastNotification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorInvoices;
